import { ErrorMessage, Field, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { X } from "react-feather";
import * as Yup from "yup";
import TransparentLoader from "../../components/transparent-loader/TransparentLoader";
import { getPincodeServiceabilityApi } from "../../redux/sagas/requests/master/pincodeRequest";
import { AppState } from "../../redux/types";
import { apiRequestHandler } from "../../utils/commonFunctions";

interface IPincodeServiceabilityFormValues {
    originPincode: string;
    destinationPincode: string;
}

interface IPincodeServiceabilityResult {
    origin?: {
        pincode?: number;
        pickup?: string;
        district_name?: string;
    };
    destination?: {
        pincode?: number;
        delivery?: string;
        district_name?: string;
    };
}

interface IRecentSearchItem {
    originPincode: string;
    destinationPincode: string;
}

const RECENT_SEARCHES_KEY = "pincode-serviceability-recent-searches";

export default function PincodeServiceability() {
    const isLoader = useSelector((state: AppState) => state.loader.transparentLoader);
    const [result, setResult] = useState<IPincodeServiceabilityResult | null>(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [hasSearched, setHasSearched] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const [recentSearches, setRecentSearches] = useState<IRecentSearchItem[]>([]);

    const initialValues: IPincodeServiceabilityFormValues = {
        originPincode: "",
        destinationPincode: "",
    };

    const validationSchema = Yup.object().shape({
        originPincode: Yup.string()
            .required("Origin pincode is required")
            .matches(/^\d{6}$/, "Origin pincode must be exactly 6 digits"),
        destinationPincode: Yup.string()
            .required("Destination pincode is required")
            .matches(/^\d{6}$/, "Destination pincode must be exactly 6 digits"),
    });

    useEffect(() => {
        const rawSearches = localStorage.getItem(RECENT_SEARCHES_KEY);
        if (!rawSearches) {
            return;
        }

        try {
            const parsedSearches = JSON.parse(rawSearches) as IRecentSearchItem[];
            if (Array.isArray(parsedSearches)) {
                setRecentSearches(parsedSearches.slice(0, 5));
            }
        } catch (error) {
            localStorage.removeItem(RECENT_SEARCHES_KEY);
        }
    }, []);

    const updateRecentSearches = (item: IRecentSearchItem) => {
        setRecentSearches((prev) => {
            const deduped = prev.filter(
                (searchItem) =>
                    !(
                        searchItem.originPincode === item.originPincode &&
                        searchItem.destinationPincode === item.destinationPincode
                    )
            );
            const updated = [item, ...deduped].slice(0, 5);
            localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
            return updated;
        });
    };

    const fetchServiceability = async (values: IPincodeServiceabilityFormValues, shouldSaveRecent = true) => {
        setHasSearched(true);
        setErrorMessage("");
        setResult(null);
        setIsChecking(true);

        const response = await apiRequestHandler(getPincodeServiceabilityApi, {
            origin_pincode: values.originPincode,
            destination_pincode: values.destinationPincode,
        });
        setIsChecking(false);

        if (response?.status) {
            setResult(response.data ?? null);
            if (shouldSaveRecent) {
                updateRecentSearches(values);
            }
            return;
        }

        setErrorMessage(response?.message ?? "Unable to fetch pincode serviceability.");
    };

    const handleSubmit = async (values: IPincodeServiceabilityFormValues) => {
        await fetchServiceability(values, true);
    };

    const getServiceabilityStatus = () => {
        if (!result) {
            return null;
        }

        const pickup = (result.origin?.pickup ?? "").toUpperCase();
        const delivery = (result.destination?.delivery ?? "").toUpperCase();
        const isNotServiceable = pickup === "NOT-SERVICEABLE" || delivery === "NOT-SERVICEABLE";

        if (isNotServiceable) {
            return { label: "Not Serviceable", className: "bg-red-100 text-red-700" };
        }

        return { label: "Serviceable", className: "bg-green-100 text-green-700" };
    };

    const statusBadge = getServiceabilityStatus();

    return (
        <>
            <TransparentLoader loading={isLoader} />

            <main className="min-h-screen flex items-start justify-center bg-gray-50 pt-6 pb-28 px-4 sm:px-6 sm:pb-6">
                <div className="w-full max-w-6xl bg-white shadow-2xl rounded-2xl p-4 sm:p-6 md:p-10 space-y-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <img
                            src="http://prod-tqs.s3.ap-south-1.amazonaws.com/vendor_logo/tqs-logo.jpeg"
                            alt="Logo"
                            className="h-12 sm:h-16 w-auto"
                        />
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-sm text-gray-600">
                            <a href="tel:01141518777" className="flex items-center gap-2 hover:text-red-600 transition">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2 5.5C2 4.12 3.12 3 4.5 3h1.6c.74 0 1.38.52 1.54 1.24l.64 2.88c.14.62-.18 1.26-.76 1.5l-1.2.5a16.02 16.02 0 007.22 7.22l.5-1.2c.24-.58.88-.9 1.5-.76l2.88.64c.72.16 1.24.8 1.24 1.54v1.6c0 1.38-1.12 2.5-2.5 2.5C8.82 21 2 14.18 2 5.5z" />
                                </svg>
                                <span>011-41518777</span>
                            </a>
                            <a href="mailto:info@tqslogistic.com" className="flex items-center gap-2 hover:text-red-600 transition">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l9 6 9-6M4 6h16a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V7a1 1 0 011-1z" />
                                </svg>
                                <span>info@tqslogistic.com</span>
                            </a>
                        </div>
                    </div>

                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-gray-800">
                        Pincode Serviceability
                    </h1>
                    <p className="text-center text-sm sm:text-base text-gray-600">
                        Enter origin and destination pincodes to check serviceability.
                    </p>

                    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                        {({ values, setFieldValue }) => (
                            <Form id="pincode-serviceability-form" className="space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-[260px_46px_260px_120px] gap-4 sm:gap-6 items-center sm:mx-auto sm:w-fit">
                                    <div>
                                        <label htmlFor="originPincode" className="tqs-input-label sm:hidden">
                                            Origin Pincode*
                                        </label>
                                        <div className="relative">
                                            <Field
                                                id="originPincode"
                                                name="originPincode"
                                                type="text"
                                                maxLength={6}
                                                className="tqs-input h-[46px] pr-11 text-[15px]"
                                                placeholder="Enter origin pincode"
                                            />
                                            {values.originPincode && (
                                                <button
                                                    type="button"
                                                    onClick={() => setFieldValue("originPincode", "")}
                                                    className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
                                                    aria-label="Clear origin pincode"
                                                >
                                                    <X size={18} />
                                                </button>
                                            )}
                                        </div>
                                        <div className="mt-1 min-h-[18px] sm:hidden">
                                            <ErrorMessage component="div" name="originPincode" className="input-error" />
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-center">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const currentOrigin = values.originPincode;
                                                const currentDestination = values.destinationPincode;
                                                setFieldValue("originPincode", currentDestination);
                                                setFieldValue("destinationPincode", currentOrigin);
                                            }}
                                            className="group h-11 w-11 rounded-full border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:border-red-300 active:scale-95 transition-all duration-200 flex items-center justify-center shadow-sm focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-2"
                                            aria-label="Swap origin and destination pincodes"
                                            title="Swap pincodes"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="h-4 w-4 transition-transform duration-200 group-hover:rotate-180"
                                                aria-hidden="true"
                                            >
                                                <path d="M4 8h13" />
                                                <path d="m13 4 4 4-4 4" />
                                                <path d="M20 16H7" />
                                                <path d="m11 12-4 4 4 4" />
                                            </svg>
                                        </button>
                                    </div>

                                    <div>
                                        <label htmlFor="destinationPincode" className="tqs-input-label sm:hidden">
                                            Destination Pincode*
                                        </label>
                                        <div className="relative">
                                            <Field
                                                id="destinationPincode"
                                                name="destinationPincode"
                                                type="text"
                                                maxLength={6}
                                                className="tqs-input h-[46px] pr-11 text-[15px]"
                                                placeholder="Enter destination pincode"
                                            />
                                            {values.destinationPincode && (
                                                <button
                                                    type="button"
                                                    onClick={() => setFieldValue("destinationPincode", "")}
                                                    className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
                                                    aria-label="Clear destination pincode"
                                                >
                                                    <X size={18} />
                                                </button>
                                            )}
                                        </div>
                                        <div className="mt-1 min-h-[18px] sm:hidden">
                                            <ErrorMessage component="div" name="destinationPincode" className="input-error" />
                                        </div>
                                    </div>

                                    <div className="hidden sm:flex items-center">
                                        <button
                                            type="submit"
                                            className="h-[46px] min-w-[130px] rounded-lg bg-[#e53940] px-8 text-base font-semibold text-white transition-colors duration-200 hover:bg-[#d63038]"
                                        >
                                            Check
                                        </button>
                                    </div>
                                </div>

                                <div className="hidden sm:grid sm:grid-cols-[260px_46px_260px_120px] sm:gap-6 sm:mx-auto sm:w-fit">
                                    <div className="min-h-[18px]">
                                        <ErrorMessage component="div" name="originPincode" className="input-error mt-1" />
                                    </div>
                                    <div />
                                    <div className="min-h-[18px]">
                                        <ErrorMessage component="div" name="destinationPincode" className="input-error mt-1" />
                                    </div>
                                    <div />
                                </div>

                                {recentSearches.length > 0 && (
                                    <div className="pt-1">
                                        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Recent Searches</p>
                                        <div className="flex flex-wrap gap-2">
                                            {recentSearches.map((item) => (
                                                <button
                                                    key={`${item.originPincode}-${item.destinationPincode}`}
                                                    type="button"
                                                    onClick={async () => {
                                                        setFieldValue("originPincode", item.originPincode);
                                                        setFieldValue("destinationPincode", item.destinationPincode);
                                                        await fetchServiceability(item, false);
                                                    }}
                                                    className="rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:border-red-400 hover:text-red-600"
                                                >
                                                    {item.originPincode} → {item.destinationPincode}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </Form>
                        )}
                    </Formik>

                    {errorMessage && <p className="mt-6 text-center text-sm font-medium text-red-600">{errorMessage}</p>}

                    {isChecking && (
                        <div className="mt-10 border rounded-xl overflow-hidden shadow animate-pulse">
                            <div className="bg-gray-200 p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="space-y-3">
                                    <div className="h-5 w-28 rounded bg-gray-300" />
                                    <div className="h-4 w-40 rounded bg-gray-300" />
                                    <div className="h-4 w-36 rounded bg-gray-300" />
                                    <div className="h-4 w-48 rounded bg-gray-300" />
                                </div>
                                <div className="space-y-3">
                                    <div className="h-5 w-32 rounded bg-gray-300" />
                                    <div className="h-4 w-40 rounded bg-gray-300" />
                                    <div className="h-4 w-36 rounded bg-gray-300" />
                                    <div className="h-4 w-48 rounded bg-gray-300" />
                                </div>
                            </div>
                        </div>
                    )}

                    {!hasSearched && !isChecking && (
                        <div className="mt-10 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-white text-2xl shadow-sm">
                                🔎
                            </div>
                            <p className="text-sm font-medium text-gray-700">No search yet</p>
                            <p className="mt-1 text-xs text-gray-500">Enter both pincodes and tap Check to view serviceability.</p>
                        </div>
                    )}

                    {result && !isChecking && (
                        <section className="mt-10 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                            <div className="flex flex-col gap-3 border-b border-gray-200 bg-gray-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                                <div>
                                    <h2 className="text-base font-semibold text-gray-800 sm:text-lg">Serviceability Result</h2>
                                    <p className="text-xs text-gray-500 sm:text-sm">Origin and destination details fetched successfully.</p>
                                </div>
                                {statusBadge && (
                                    <span className={`inline-flex w-fit rounded-full px-3 py-1 text-lg font-semibold ${statusBadge.className}`}>
                                        {statusBadge.label}
                                    </span>
                                )}
                            </div>

                            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 sm:gap-6 sm:p-6">
                                <article className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                    <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-800">
                                        <span aria-hidden="true" className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-blue-50 text-blue-600">📍</span>
                                        Origin
                                    </h3>
                                    <div className="space-y-2 text-sm text-gray-700">
                                        <div className="flex items-center justify-between gap-3 border-b border-gray-100 pb-2">
                                            <span className="font-medium text-gray-500">Pincode</span>
                                            <span className="font-semibold text-gray-900">{result.origin?.pincode ?? "-"}</span>
                                        </div>
                                        <div className="flex items-center justify-between gap-3 border-b border-gray-100 pb-2">
                                            <span className="font-medium text-gray-500">Pickup Type</span>
                                            <span className="font-semibold text-gray-900">{result.origin?.pickup ?? "-"}</span>
                                        </div>
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="font-medium text-gray-500">District</span>
                                            <span className="font-semibold text-gray-900">{result.origin?.district_name ?? "-"}</span>
                                        </div>
                                    </div>
                                </article>

                                <article className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                    <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-800">
                                        <span aria-hidden="true" className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-orange-50 text-orange-600">🚚</span>
                                        Destination
                                    </h3>
                                    <div className="space-y-2 text-sm text-gray-700">
                                        <div className="flex items-center justify-between gap-3 border-b border-gray-100 pb-2">
                                            <span className="font-medium text-gray-500">Pincode</span>
                                            <span className="font-semibold text-gray-900">{result.destination?.pincode ?? "-"}</span>
                                        </div>
                                        <div className="flex items-center justify-between gap-3 border-b border-gray-100 pb-2">
                                            <span className="font-medium text-gray-500">Delivery Type</span>
                                            <span className="font-semibold text-gray-900">{result.destination?.delivery ?? "-"}</span>
                                        </div>
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="font-medium text-gray-500">District</span>
                                            <span className="font-semibold text-gray-900">{result.destination?.district_name ?? "-"}</span>
                                        </div>
                                    </div>
                                </article>
                            </div>
                        </section>
                    )}
                </div>
            </main>

            <div className="fixed inset-x-0 bottom-0 z-20 border-t border-gray-200 bg-white/95 px-4 py-3 shadow-2xl backdrop-blur sm:hidden">
                <button
                    type="submit"
                    form="pincode-serviceability-form"
                    className="h-[52px] w-full rounded-xl bg-[#e53940] text-base font-semibold text-white transition-colors duration-200 hover:bg-[#d63038]"
                >
                    Check
                </button>
            </div>
        </>
    );
}